/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Master Playback Transport Bar (Task 10.5.6)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useEffect, useRef } from 'react';
import {
  Play,
  Pause,
  RotateCcw,
  RotateCw,
  Repeat,
  Volume2,
  VolumeX,
  Download,
  Gauge,
} from 'lucide-react';
import { useTranslation } from '../../i18n';
import {
  useSubtitleStudioStore,
  formatTimecode,
} from '../../stores/useSubtitleStudioStore';

export const SubtitleTransportBar: React.FC = () => {
  const { t } = useTranslation();
  const audioElementRef = useRef<HTMLAudioElement | null>(null);

  const {
    audioFile,
    isPlaying,
    currentTime,
    playbackRate,
    volume,
    isMuted,
    loopSelectedCue,
    selectedCueId,
    cues,
    play,
    pause,
    seek,
    setPlaybackRate,
    setVolume,
    toggleMute,
    toggleLoopSelectedCue,
    setExportModalOpen,
  } = useSubtitleStudioStore();

  const totalDuration = Math.max(1, audioFile?.durationSec || 36.5);

  // Synchronize synthetic or actual audio element with store timer
  useEffect(() => {
    let interval: ReturnType<typeof setInterval>;
    if (isPlaying) {
      interval = setInterval(() => {
        const nextTime = currentTime + 0.1 * playbackRate;

        // Handle loop selected cue
        if (loopSelectedCue && selectedCueId) {
          const cue = cues.find((c) => c.id === selectedCueId);
          if (cue && nextTime >= cue.end) {
            seek(cue.start);
            return;
          }
        }

        if (nextTime >= totalDuration) {
          seek(0);
          pause();
        } else {
          seek(nextTime);
        }
      }, 100);
    }
    return () => clearInterval(interval);
  }, [isPlaying, currentTime, playbackRate, totalDuration, loopSelectedCue, selectedCueId, cues]);

  const handleSkip = (seconds: number) => {
    seek(Math.max(0, Math.min(totalDuration, currentTime + seconds)));
  };

  return (
    <div className="bg-surface rounded-2xl border border-border p-4 shadow-sm flex flex-wrap items-center justify-between gap-4">
      {/* Audio element for local preview */}
      <audio
        ref={audioElementRef}
        src={audioFile?.url}
        className="hidden"
        onEnded={() => {
          pause();
          seek(0);
        }}
      />

      {/* Left: Playback Controls */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => handleSkip(-5)}
          className="p-2 rounded-xl border border-border hover:bg-surface-subtle text-text transition-colors"
          title="Rewind 5s"
        >
          <RotateCcw className="size-4" />
        </button>

        <button
          type="button"
          onClick={() => (isPlaying ? pause() : play())}
          className="p-3 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 transition-all shadow-sm"
          title={isPlaying ? 'Pause' : 'Play'}
        >
          {isPlaying ? <Pause className="size-5 fill-current" /> : <Play className="size-5 fill-current" />}
        </button>

        <button
          type="button"
          onClick={() => handleSkip(5)}
          className="p-2 rounded-xl border border-border hover:bg-surface-subtle text-text transition-colors"
          title="Forward 5s"
        >
          <RotateCw className="size-4" />
        </button>

        <button
          type="button"
          onClick={toggleLoopSelectedCue}
          className={`p-2 rounded-xl border transition-colors ${
            loopSelectedCue
              ? 'bg-primary/10 border-primary text-primary'
              : 'border-border hover:bg-surface-subtle text-text'
          }`}
          title={t('subtitles.loopCueBtn')}
        >
          <Repeat className="size-4" />
        </button>
      </div>

      {/* Center: Timecode Display */}
      <div className="flex items-center gap-2 bg-surface-subtle px-4 py-2 rounded-xl border border-border font-mono text-sm font-semibold">
        <span className="text-primary">{formatTimecode(currentTime)}</span>
        <span className="text-text-muted">/</span>
        <span className="text-text">{formatTimecode(totalDuration)}</span>
      </div>

      {/* Right: Speed, Volume & Export Button */}
      <div className="flex items-center gap-3">
        {/* Playback Speed */}
        <div className="flex items-center gap-1.5 text-xs text-text">
          <Gauge className="size-4 text-text-muted hidden sm:inline" />
          <select
            value={playbackRate}
            onChange={(e) => setPlaybackRate(parseFloat(e.target.value))}
            className="rounded-lg border border-border bg-surface px-2 py-1.5 text-xs text-text focus:outline-none focus:ring-1 focus:ring-primary font-mono"
          >
            <option value="0.5">0.5x</option>
            <option value="0.75">0.75x</option>
            <option value="1.0">1.0x</option>
            <option value="1.25">1.25x</option>
            <option value="1.5">1.5x</option>
            <option value="2.0">2.0x</option>
          </select>
        </div>

        {/* Volume Slider */}
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={toggleMute}
            className="p-1.5 rounded-lg hover:bg-surface-subtle text-text transition-colors"
          >
            {isMuted || volume === 0 ? (
              <VolumeX className="size-4 text-text-muted" />
            ) : (
              <Volume2 className="size-4 text-primary" />
            )}
          </button>
          <input
            type="range"
            min="0"
            max="1"
            step="0.05"
            value={isMuted ? 0 : volume}
            onChange={(e) => setVolume(parseFloat(e.target.value))}
            className="w-16 sm:w-20 accent-primary cursor-pointer h-1.5 bg-border rounded-lg"
          />
        </div>

        {/* Export Modal Launch */}
        <button
          type="button"
          onClick={() => setExportModalOpen(true)}
          className="px-4 py-2 text-xs font-semibold rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 transition-all shadow-sm flex items-center gap-2"
        >
          <Download className="size-4" />
          <span className="hidden sm:inline">{t('subtitles.tabExport')}</span>
        </button>
      </div>
    </div>
  );
};
